import { Injectable } from '@angular/core';
import { updateRequestStatus } from '@ngneat/elf-requests';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import {
	deleteEntities, getAllEntities, getEntity,
	setActiveId,
	upsertEntities
} from '@ngneat/elf-entities';
import { combineLatest, Observable, of } from 'rxjs';

import { CuttingList } from '~shared/types';
import { sortByUlid } from '~shared/helpers';
import { CuttingListService } from '~core/services/cutting-list.service';
import { CuttingListLinesRepository } from '~modules/configurator/store/cutting-list-lines/cutting-list-lines.repository';

import { cuttingListStore } from './cutting-lists.store';
import { cuttingListsSelector } from './cutting-lists.selectors';

@Injectable({
	providedIn: 'root',
})
export class CuttingListsRepository {
	public cuttingLists$ = cuttingListsSelector.cuttingLists$;
	public cuttingListsLoading$ = cuttingListsSelector.cuttingListsLoading$;
	public cuttingListUpdateLoading$ = cuttingListsSelector.cuttingListUpdateLoading$;
	public activeCuttingList$ = cuttingListsSelector.activeCuttingList$;

	private cache: Map<string, CuttingList[]> = new Map();

	constructor(
		private readonly cuttingListService: CuttingListService,
		private readonly cuttingListLinesRepository: CuttingListLinesRepository
	) {}

	// Simple fetch for the projects overview (no lines, no temp entry)
	public getCuttingLists$(projectId: string): Observable<CuttingList[]> {
		return this.cuttingListService.getCuttingLists(projectId).pipe(
			take(1),
			tap((lists) => this.cache.set(projectId, lists)),
			catchError(() => {
				this.cache.set(projectId, []);
				return of([]);
			})
		);
	}

	public queryCuttingLists(projectId: string): CuttingList[] {
		return this.cache.get(projectId) ?? [];
	}

	public hasCuttingLists(projectId: string): boolean {
		return this.cache.has(projectId);
	}

	// Full fetch for the configurator (with lines and temp entry)
	public getAll(projectId: string): Observable<CuttingList[]> {
		cuttingListStore.update(updateRequestStatus('get-cutting-lists', 'pending'));
		return this.cuttingListService
			.getCuttingLists(projectId)
			.pipe(
				take(1),
				switchMap((cuttingLists: CuttingList[]) => {
					if (cuttingLists.length === 0) {
						return of([{
							id: 'temp',
							projectId: projectId,
							name: 'Zaaglijst 001',
							amountOfLines: 0,
							project: null,
							cuttingListLines: []
						}]);
					}

					return combineLatest(cuttingLists.map((cuttingList) => {
						return this.cuttingListService.getCuttingListLines(cuttingList.id).pipe(take(1), map((cuttingListLines) => ({
							...cuttingList,
							amountOfLines: cuttingListLines.length,
							cuttingListLines
						})))
					}))
				}),
				tap((cuttingLists: CuttingList[]) => {
					cuttingListStore.update(
						upsertEntities(cuttingLists),
						setActiveId(cuttingLists?.[0]?.id),
						updateRequestStatus('get-cutting-lists', 'success')
					);
					cuttingLists.forEach((cuttingList) => {
						this.cuttingListLinesRepository.upsertLines(cuttingList.cuttingListLines);
					});
				})
			)
	}

	public create(projectId: string, body): Observable<CuttingList> {
		cuttingListStore.update(updateRequestStatus('update-cutting-list', 'pending'));
		return this.cuttingListService
			.createCuttingList(projectId, body)
			.pipe(
				take(1),
				tap((cuttingList) => {
					cuttingListStore.update(
						deleteEntities('temp'),
						upsertEntities(cuttingList),
						updateRequestStatus('update-cutting-list', 'success')
					)
				})
			)
	}

	public update(projectId: string, cuttingListId: string, body): Observable<CuttingList> {
		cuttingListStore.update(updateRequestStatus('update-cutting-list', 'pending'));
		return this.cuttingListService
			.updateCuttingList(projectId, cuttingListId, body)
			.pipe(
				take(1),
				tap((cuttingList) => {
					cuttingListStore.update(
						upsertEntities(cuttingList),
						updateRequestStatus('update-cutting-list', 'success')
					)
				})
			)
	}

	public duplicate(projectId: string, cuttingListId: string): Observable<CuttingList> {
		cuttingListStore.update(updateRequestStatus('update-cutting-list', 'pending'));
		return this.cuttingListService
			.duplicateCuttingList(projectId, cuttingListId)
			.pipe(
				take(1),
				switchMap((cuttingList) =>
					this.cuttingListService.getCuttingListLines(cuttingList.id).pipe(
						take(1),
						map((cuttingListLines) => ({
							...cuttingList,
							amountOfLines: cuttingListLines.length,
							cuttingListLines
						}))
					)
				),
				tap((cuttingList) => {
					cuttingListStore.update(
						upsertEntities(cuttingList),
						updateRequestStatus('update-cutting-list', 'success')
					);
					this.cuttingListLinesRepository.upsertLines(cuttingList.cuttingListLines);
				})
			)
	}

	public delete(projectId: string, cuttingListId: string): Observable<void> {
		cuttingListStore.update(updateRequestStatus('update-cutting-list', 'pending'));
		return this.cuttingListService
			.deleteCuttingList(projectId, cuttingListId)
			.pipe(
				take(1),
				tap(() => {
					cuttingListStore.update(
						deleteEntities(cuttingListId),
						updateRequestStatus('update-cutting-list', 'success')
					);
					const remaining = sortByUlid(cuttingListStore.query(getAllEntities()));
					if (remaining.length > 0) {
						cuttingListStore.update(setActiveId(remaining[0].id));
					}
				})
			)
	}

	public updateActiveCuttingList(): void {
		const activeCuttingListId = this.queryActiveCuttingListId();
		const activeCuttingList = cuttingListStore
			.query(getAllEntities())
			.find((cuttingList) => cuttingList.id === activeCuttingListId);
		if (activeCuttingList) {
			const cuttingListLines = this.cuttingListLinesRepository.queryCuttingListsLines()
				.filter((line) => line.cuttingListId === activeCuttingListId);
			const updatedCuttingList = {
				...activeCuttingList,
				amountOfLines: cuttingListLines.length,
				cuttingListLines: cuttingListLines
			};
			cuttingListStore.update(upsertEntities(updatedCuttingList));
		}
	}

	public activateCuttingList(cuttingListId: string): void {
		cuttingListStore.update(
			setActiveId(cuttingListId)
		)
	}

	/**
	 * The list the cutting list tab opens: the one asked for when it belongs to no part,
	 * else the first list that belongs to no part, else none. A part's lists are opened from
	 * the part.
	 */
	public queryUnassignedCuttingListId(preferredId?: string | null): string | null {
		const lists = sortByUlid(cuttingListStore.query(getAllEntities()) as CuttingList[])
			.filter((cuttingList) => !cuttingList.partId);
		return (lists.find((cuttingList) => cuttingList.id === preferredId) ?? lists[0])?.id ?? null;
	}

	public queryCuttingList(cuttingListId: string): CuttingList | undefined {
		return cuttingListStore.query(getEntity(cuttingListId)) as CuttingList | undefined;
	}

	public queryActiveCuttingListId(): string | undefined {
		return cuttingListStore.query((store) => store.activeId);
	}

	public replaceBoardSku$(cuttingListId: string, boardSku: string, body): Observable<string> {
		return this.cuttingListService.replaceBoardSku(cuttingListId, boardSku, body).pipe(
			take(1),
			switchMap((message) =>
				this.cuttingListLinesRepository.getAll(cuttingListId).pipe(map(() => message))
			)
		);
	}

	public replaceBoardSkuByProject$(projectId: string, boardSku: string, body): Observable<string> {
		return this.cuttingListService.replaceBoardSkuByProject(projectId, boardSku, body).pipe(
			take(1),
			switchMap((message) =>
				this.getAll(projectId).pipe(map(() => message))
			)
		);
	}

	public replaceBoardByPanelGroupForProject$(projectId: string, panelGroup: string, body): Observable<string> {
		return this.cuttingListService.replaceBoardByPanelGroupForProject(projectId, panelGroup, body).pipe(
			take(1),
			switchMap((message) =>
				this.getAll(projectId).pipe(map(() => message))
			)
		);
	}

	public regenerateBomLines$(cuttingListId: string): Observable<string> {
		return this.cuttingListService.regenerateBomLines(cuttingListId).pipe(take(1));
	}

	public reset(): void {
		cuttingListStore.reset()
	}
}